import { FolderFileEntry } from '@/types/document.types'

export const FILE_VALIDATION = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB in bytes
  ALLOWED_TYPES: {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
  },
  ALLOWED_EXTENSIONS: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
} as const;


export interface ValidationResult {
  valid: boolean;
  error?: string;
}

const getFileExtension = (filename: string): string => {
  const match = filename.match(/\.[^/.]+$/);
  return match ? match[0] : '';
};

export const validateFileSize = (file: File): ValidationResult => {
  if (file.size > FILE_VALIDATION.MAX_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `El archivo es demasiado grande (${sizeMB}MB). El tamaño máximo permitido es 10MB.`,
    };
  }
  return { valid: true };
};

export const validateFileType = (file: File): ValidationResult => {
  const fileExtension = getFileExtension(file.name).toLowerCase();

  if (!FILE_VALIDATION.ALLOWED_EXTENSIONS.includes(fileExtension as any)) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido. Solo se aceptan: PDF, Word (.doc, .docx), Excel (.xls, .xlsx) e Imágenes (.jpg, .png)`,
    };
  }

  const allowedTypes = Object.keys(FILE_VALIDATION.ALLOWED_TYPES);
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Tipo de archivo no válido. Por favor, seleccione un archivo válido.`,
    };
  }

  return { valid: true };
};


export const validateFile = (file: File): ValidationResult => {

  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  const typeValidation = validateFileType(file);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  return { valid: true };
};

export interface FolderValidationResult {
  validFiles: FolderFileEntry[];
  invalidFiles: Array<{ fileName: string; relativePath: string; error: string }>;
  totalFiles: number;
}

export const validateFolder = (entries: FolderFileEntry[]): FolderValidationResult => {
  const validFiles: FolderFileEntry[] = [];
  const invalidFiles: Array<{ fileName: string; relativePath: string; error: string }> = [];

  for (const entry of entries) {
    const validation = validateFile(entry.file);
    if (validation.valid) {
      validFiles.push(entry);
    } else {
      invalidFiles.push({
        fileName: entry.file.name,
        relativePath: entry.relativePath,
        error: validation.error || 'Archivo no valido',
      });
    }
  }

  return { validFiles, invalidFiles, totalFiles: entries.length };
};
